import { paletteLabel } from './palette'
import { isBrwnPlumeType } from './eggs/brwnPlume'
import type { ScenarioObject, ScenarioSession } from './types'

/**
 * Playable / training-team roleplay positions derived from board inventory (POC).
 * Unfilled positions are allowed — scenario playback does not depend on them yet.
 * Assignee lives on the object (`rolePlayAssigneeId`), not a parallel session map.
 */
export type RolePlaySlot = {
  id: string
  /** Canonical display name (hyphenated compounds; AAR mirrors this). */
  label: string
  objectId: string
  objectType: string
  kind: 'operator' | 'facility_rep' | 'command' | 'dispatch' | 'vendor' | 'other'
}

/** UI / toast copy: em/en dashes → hyphen. PDFs keep the canonical `slot.label`. */
export function rolePlayLabelUi(label: string): string {
  return label.replace(/[\u2013\u2014]/g, '-')
}

const VEHICLE_TYPES = new Set([
  'car',
  'truck',
  'engine',
  'ladder',
  'ems',
  'hazmat',
  'rescue',
  'rehab',
  'police',
  'work_vehicle',
  'bulldozer',
  'forestry_bulldozer',
  'backhoe',
  'boat',
  'airplane',
  'helicopter',
  'drone',
  'rollover_vehicle',
  'contractor_response',
])

const FACILITY_TYPES = new Set(['school', 'daycare', 'hospital', 'water_treatment'])

const COMMAND_TYPES = new Set([
  'icp',
  'dispatch',
  'pipeline_personnel',
  'responder',
  'operator',
  'person',
  'child',
  'incident_commander',
  'chief_officer',
  'staging',
  'media_staging',
  'muster',
  'access_point',
])

const VENDOR_TYPES = new Set(['contractor'])

const STATION_TYPES = new Set([
  'pump',
  'compressor',
  'regulator_station',
  'city_gate',
  'mercaptan_station',
  'meter_station',
])

/** Plume / release hazards that can be owned as an inject / area of responsibility. */
const HAZARD_OWNER_TYPES = new Set([
  'gas_leak',
  'vapor_cloud',
  'leak',
  'brwn_plume',
  'hot_zone',
  'pipeburst',
  'rupture',
  'exclusion',
  'release_liquids',
  'fire',
  'explosion',
  'isolation',
  'evacuate',
  'shelter',
])

/** Prefer map caption (Gas Leak 1) so roleplay matches what the board shows. */
function objectTitle(obj: ScenarioObject): string {
  const map = obj.mapLabel?.trim()
  if (map) return map
  const custom = obj.label.trim()
  const fallback = paletteLabel(obj.type)
  if (custom && custom !== fallback) return custom
  return fallback
}

/** "Gas Leak Owner" + 2 → "Gas Leak 2 Owner" when map captions are absent. */
function disambiguateRolePlayLabel(label: string, n: number): string {
  const suffixes = [' Owner', ' Administrator', ' Operator', ' Driver', ' Representative']
  for (const suffix of suffixes) {
    if (label.endsWith(suffix)) {
      return `${label.slice(0, -suffix.length)} ${n}${suffix}`
    }
  }
  return `${label} ${n}`
}

export function slotForObject(obj: ScenarioObject): RolePlaySlot | null {
  const title = objectTitle(obj)
  const type = obj.type

  if (type === 'wind' || type === 'tornado' || type === 'hurricane') {
    return {
      id: `rp:${obj.id}`,
      label: `Weather - ${title}`,
      objectId: obj.id,
      objectType: type,
      kind: 'other',
    }
  }
  if (isBrwnPlumeType(type)) {
    return {
      id: `rp:${obj.id}`,
      label: `${title} Administrator`,
      objectId: obj.id,
      objectType: type,
      kind: 'other',
    }
  }
  if (HAZARD_OWNER_TYPES.has(type)) {
    return {
      id: `rp:${obj.id}`,
      label: `${title} Owner`,
      objectId: obj.id,
      objectType: type,
      kind: 'other',
    }
  }
  if (VEHICLE_TYPES.has(type)) {
    const verb =
      type === 'airplane' || type === 'helicopter' || type === 'drone'
        ? 'Pilot'
        : type === 'backhoe' || type === 'bulldozer' || type === 'forestry_bulldozer'
          ? 'Operator'
          : 'Driver'
    return {
      id: `rp:${obj.id}`,
      label: `${title} ${verb}`,
      objectId: obj.id,
      objectType: type,
      kind: 'operator',
    }
  }
  if (FACILITY_TYPES.has(type)) {
    return {
      id: `rp:${obj.id}`,
      label: `${title} Representative`,
      objectId: obj.id,
      objectType: type,
      kind: 'facility_rep',
    }
  }
  if (VENDOR_TYPES.has(type)) {
    return {
      id: `rp:${obj.id}`,
      label: `Vendor Rep - ${title}`,
      objectId: obj.id,
      objectType: type,
      kind: 'vendor',
    }
  }
  if (
    type === 'incident_commander' ||
    type === 'chief_officer' ||
    type === 'pipeline_personnel' ||
    type === 'responder' ||
    type === 'operator' ||
    type === 'person' ||
    type === 'child'
  ) {
    return {
      id: `rp:${obj.id}`,
      label: title,
      objectId: obj.id,
      objectType: type,
      kind: 'command',
    }
  }
  if (type === 'dispatch') {
    return {
      id: `rp:${obj.id}`,
      label: title,
      objectId: obj.id,
      objectType: type,
      kind: 'dispatch',
    }
  }
  // ICP is a command post / role — not "Dispatcher". Script injects often author
  // as the ICP object; Dispatch is a separate palette type with its own seat.
  if (type === 'icp') {
    return {
      id: `rp:${obj.id}`,
      label: title,
      objectId: obj.id,
      objectType: type,
      kind: 'command',
    }
  }
  if (COMMAND_TYPES.has(type)) {
    return {
      id: `rp:${obj.id}`,
      label: title,
      objectId: obj.id,
      objectType: type,
      kind: 'other',
    }
  }
  if (STATION_TYPES.has(type)) {
    return {
      id: `rp:${obj.id}`,
      label: `${title} Operator`,
      objectId: obj.id,
      objectType: type,
      kind: 'operator',
    }
  }
  return null
}

/** Default on — omit or true means use roleplay positions. */
export function isRolePlayEnabled(session: ScenarioSession): boolean {
  return session.rolePlayEnabled !== false
}

/** Build the role-play roster for the current board (deterministic order). */
export function rolePlaySlotsFromSession(session: ScenarioSession): RolePlaySlot[] {
  const raw = session.objects
    .map(slotForObject)
    .filter((s): s is RolePlaySlot => s != null)

  const labelCounts = new Map<string, number>()
  for (const s of raw) labelCounts.set(s.label, (labelCounts.get(s.label) ?? 0) + 1)

  const labelSeen = new Map<string, number>()
  const slots = raw.map((s) => {
    const total = labelCounts.get(s.label) ?? 1
    if (total <= 1) return s
    const n = (labelSeen.get(s.label) ?? 0) + 1
    labelSeen.set(s.label, n)
    return { ...s, label: disambiguateRolePlayLabel(s.label, n) }
  })

  return slots.sort((a, b) => a.label.localeCompare(b.label))
}

type RolePlayPersonKey = { id: string; userId?: string; email?: string; isDeleted?: boolean }

function assigneeKey(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase()
}

/** Roster row for a stored seat key: roster id, or a legacy user id or email still on an unrepaired row. */
export function personForRolePlayAssignee<T extends RolePlayPersonKey>(
  people: readonly T[],
  assigneeId: string,
): T | undefined {
  const key = assigneeKey(assigneeId)
  if (!key) return undefined
  return people.find((person) => {
    if (person.isDeleted) return false
    return (
      assigneeKey(person.id) === key ||
      assigneeKey(person.userId) === key ||
      assigneeKey(person.email) === key
    )
  })
}

export function slotAssigneeId(session: ScenarioSession, slot: RolePlaySlot): string | undefined {
  if (!slot.objectId) return undefined
  const id = session.objects.find((o) => o.id === slot.objectId)?.rolePlayAssigneeId?.trim()
  return id || undefined
}

/** Clear every seat held by this person (e.g. soft-remove from roster). */
export function clearPersonRolePlaySeats(session: ScenarioSession, personIds: ReadonlySet<string>): ScenarioSession {
  let changed = false
  const objects = session.objects.map((o) => {
    const aid = o.rolePlayAssigneeId
    if (!aid || !personIds.has(aid)) return o
    changed = true
    const next = { ...o }
    delete next.rolePlayAssigneeId
    return next
  })
  return changed ? { ...session, objects } : session
}

/**
 * Assign (or clear) the roleplay seat for one object.
 * A person may hold only one seat — other objects lose that assignee.
 */
export function withRolePlayAssignee(
  session: ScenarioSession,
  objectId: string,
  personId: string | null,
): ScenarioSession {
  const objects = session.objects.map((o) => {
    if (personId && o.rolePlayAssigneeId === personId && o.id !== objectId) {
      const cleared = { ...o }
      delete cleared.rolePlayAssigneeId
      return cleared
    }
    if (o.id !== objectId) return o
    if (!personId) {
      if (!o.rolePlayAssigneeId) return o
      const cleared = { ...o }
      delete cleared.rolePlayAssigneeId
      return cleared
    }
    return { ...o, rolePlayAssigneeId: personId }
  })
  return { ...session, objects }
}

export function rolePlayAssigneeName(
  session: ScenarioSession,
  slot: RolePlaySlot,
  people: readonly { id: string; userId?: string; name: string; email?: string }[],
  opts?: { omitContactDetails?: boolean },
): string | null {
  const personId = slotAssigneeId(session, slot)
  if (!personId) return null
  const p = people.find((x) => (x.userId || x.id) === personId)
  if (!p) return null
  const name = p.name.trim()
  if (name) return name
  if (opts?.omitContactDetails) return 'Participant'
  return p.email?.trim() || null
}

/** Slot id → person id for comment attribution and similar lookups. */
export function rolePlayAssignmentMap(session: ScenarioSession): Record<string, string> {
  const out: Record<string, string> = {}
  for (const slot of rolePlaySlotsFromSession(session)) {
    const aid = slotAssigneeId(session, slot)
    if (aid) out[slot.id] = aid
  }
  return out
}
